import Loading from 'vue-loading-overlay';
import MainLayout from '@/components/layout/admin/MainLayout.vue';
import Select2Multiple from 'v-select2-multiple-component';
import AdvancedLaravelVuePaginate from 'advanced-laravel-vue-paginate';
import 'advanced-laravel-vue-paginate/dist/advanced-laravel-vue-paginate.css';
import 'vue-loading-overlay/dist/vue-loading.css';
import '@/assets/plugins/css/select2.css';
import '@/assets/css/report.css';
import store from '@/store';
import axios from 'axios';
import SelectAllService from '@/services/SelectAllService.js';
import CONFIG from '@/assets/js/config.custom.js';

export default {
  name: 'ClassScoreSummary',
  components: {
    Loading,
    MainLayout,
    Select2Multiple,
    AdvancedLaravelVuePaginate,
  },
  mixins: [SelectAllService],

  data() {
    return {
      isFilterOpen: false,
      isLoading: false,
      reportData: null,
      columns: [],
      rows: [],
      sortByField: 'student_no',
      sortOrder: 'asc',
      parentPeriodArr: [],
      selectedParentPeriod: null,
      termArr: [],
      selectedTermId: null,
      gradeArr: [],
      selectedCampusGradeId: null,
      classArr: [],
      selectedClassId: null,
      searchData: null,
      perPage: 20,
      config: CONFIG,
    };
  },

  computed: {
    classAverage() {
      return this.reportData ? this.reportData.class_average : 0;
    },
    highestMark() {
      return this.reportData ? this.reportData.highest_mark : 0;
    },
    lowestMark() {
      return this.reportData ? this.reportData.lowest_mark : 0;
    },
    studentCount() {
      return this.reportData ? this.reportData.student_count : 0;
    },
    averageCardLabel() {
      if (!this.reportData || !this.reportData.level) return 'Average Score';
      if (this.reportData.level === 'term' || this.reportData.level === 'academic_year') return 'Average Score';
      return this.reportData.level === 'grade' ? 'Grade Average' : 'Class Average';
    },
    sortedRows() {
      if (!this.rows.length) return [];
      if (this.isPaginated) return this.rows;
      const field = this.sortByField;
      const order = this.sortOrder;
      return [...this.rows].sort((a, b) => {
        const va = a[field];
        const vb = b[field];
        const isNum = typeof va === 'number' && typeof vb === 'number';
        let cmp = 0;
        if (va == null && vb == null) cmp = 0;
        else if (va == null) cmp = 1;
        else if (vb == null) cmp = -1;
        else if (isNum) cmp = va - vb;
        else cmp = String(va).localeCompare(String(vb));
        return order === 'asc' ? cmp : -cmp;
      });
    },
    isPaginated() {
      return this.reportData && this.reportData.current_page != null;
    },
    scoreSummaryPagination() {
      if (!this.reportData || this.reportData.current_page == null) return {};
      return {
        data: this.reportData.data || this.reportData.students || [],
        current_page: this.reportData.current_page,
        per_page: this.reportData.per_page,
        total: this.reportData.total,
        last_page: this.reportData.last_page,
        from: this.reportData.from,
        to: this.reportData.to,
      };
    },
  },

  watch: {
    selectedParentPeriod(acdYearId) {
      this.termArr = [];
      this.selectedTermId = null;
      this.gradeArr = [];
      this.selectedCampusGradeId = null;
      this.classArr = [];
      this.selectedClassId = null;
      if (acdYearId) this.getTermPeriod(acdYearId);
      if (acdYearId) this.getGradeListByAcdYear(acdYearId);
    },

    selectedCampusGradeId(campusGradeId) {
      this.classArr = [];
      this.selectedClassId = null;
      if (campusGradeId) this.getClassByCampusGrade(campusGradeId);
    },
  },

  methods: {
    clearReport() {
      this.reportData = null;
      this.rows = [];
      this.columns = [];
      this.searchData = null;
      this.resetSelectedAll();
      this.currentCbs = [];
    },

    rowKey(row) {
      return `${row.student_id}::${row.class_id}`;
    },

    /**
     * Rebuild visible checkbox state from persisted selection (selectedIds / selectedAllToggle).
     * Does not clear selection — call resetSelectedAll() only when starting a new search or refresh.
     */
    initCheckboxesForRows() {
      const next = [];
      this.sortedRows.forEach((row) => {
        next.push(this.isRowChecked(this.rowKey(row)));
      });
      this.currentCbs = next;
      this.syncSelectAllHeaderCheckbox();
    },

    /**
     * Header “select all” is checked only in true select-all mode with no exclusions.
     * - Manual row checks (!selectedAllToggle) never check the header (avoids auto-check when ticking every row on a page).
     * - Select-all with any row unchecked anywhere keeps the header off on all pages.
     */
    syncSelectAllHeaderCheckbox() {
      const itemCount = this.sortedRows.length;
      if (itemCount === 0) {
        this.selectedAllCb = false;
        return;
      }
      if (this.selectedAllToggle) {
        this.selectedAllCb = this.selectedIds.length === 0;
      } else {
        this.selectedAllCb = this.selectedIds.length === itemCount && itemCount > 0;
      }
    },

    /** Override mixin: header state comes from syncSelectAllHeaderCheckbox. */
    selectAll() {
      if (this.selectedAllCb) {
        this.selectedAllToggle = false;
        this.selectedIds = [];
        this.selectedAllCb = false;
      } else {
        this.selectedAllToggle = true;
        this.selectedIds = [];
        this.selectedAllCb = true;
      }

      this.currentCbs = Array(this.currentCbs.length).fill(this.selectedAllCb);
      this.syncSelectAllHeaderCheckbox();
    },

    selectToggle(id, index) {
      this.selectToggleWithCount(id, index, this.sortedRows.length);
      this.syncSelectAllHeaderCheckbox();
    },

    hasBulkSelection() {
      const hasExplicit = Array.isArray(this.selectedIds) && this.selectedIds.length > 0;
      return this.selectedAllToggle || hasExplicit;
    },

    /** Rows explicitly selected on the current (visible) page. */
    resolveSelectedItemsFromCurrentRows() {
      const out = [];
      this.sortedRows.forEach((row) => {
        const key = this.rowKey(row);
        if (this.isRowChecked(key)) {
          out.push({ student_id: row.student_id, class_id: row.class_id });
        }
      });
      return out;
    },

    /** Fetch one page of the score summary (same filters as current search). */
    async fetchScoreSummaryPage(page) {
      if (!this.searchData) return [];
      const params = { ...this.searchData };
      delete params.refresh;
      params.sort_by = this.sortByField;
      params.sort_order = this.sortOrder;
      params.page = page;
      params.per_page = this.perPage;
      const res = await axios.get('/class-score-summary/list', { params });
      const data = res.data;
      return data.students || data.data || [];
    },

    /**
     * Build payload for bulk-display: current page only, or all pages when header "select all" is on
     * (minus unchecked rows in select-all mode — same idea as payment list calculate).
     */
    /** All individually checked rows as payload (keys may span multiple pages when paginated). */
    payloadFromSelectedIds() {
      return (this.selectedIds || [])
        .map((k) => {
          const parts = String(k).split('::');
          return { student_id: Number(parts[0]), class_id: Number(parts[1]) };
        })
        .filter((x) => Number.isFinite(x.student_id) && Number.isFinite(x.class_id) && x.student_id >= 1 && x.class_id >= 1);
    },

    async resolveBulkItems() {
      if (!this.isPaginated) {
        return this.resolveSelectedItemsFromCurrentRows();
      }
      if (!this.selectedAllToggle) {
        return this.payloadFromSelectedIds();
      }

      const total = this.reportData?.total ?? 0;
      const perPage = this.perPage || this.reportData?.per_page || 10;
      const lastPage = this.reportData?.last_page ?? Math.max(1, Math.ceil(total / perPage));

      const allItems = [];
      const seen = new Set();
      for (let page = 1; page <= lastPage; page++) {
        const students = await this.fetchScoreSummaryPage(page);
        students.forEach((s) => {
          const k = `${s.student_id}::${s.class_id}`;
          if (!seen.has(k)) {
            seen.add(k);
            allItems.push({ student_id: s.student_id, class_id: s.class_id });
          }
        });
      }

      if (this.selectedIds?.length) {
        const exclude = new Set(this.selectedIds);
        return allItems.filter((item) => !exclude.has(`${item.student_id}::${item.class_id}`));
      }
      return allItems;
    },

    async applyBulkDisplay(isDisplay) {
      if (!this.hasBulkSelection()) {
        this.$notification?.error?.('Select at least one row.', { timer: 3, position: 'bottomCenter' });
        return;
      }
      const actionLabel = isDisplay ? 'Display' : 'Draft';
      if (!window.confirm(`Apply ${actionLabel} to the selected score summary row(s)?`)) {
        return;
      }
      let items;
      this.isLoading = true;
      try {
        items = await this.resolveBulkItems();
      } catch (e) {
        this.isLoading = false;
        this.$notification?.error?.('Failed to load selected rows.', { timer: 3, position: 'bottomCenter' });
        return;
      }
      if (!items.length) {
        this.isLoading = false;
        this.$notification?.error?.('Select at least one row.', { timer: 3, position: 'bottomCenter' });
        return;
      }
      axios
        .post('/class-score-summary/bulk-display', {
          status: isDisplay,
          items,
        })
        .then(() => {
          this.$notification?.success?.('Status updated successfully.', { timer: 3, position: 'bottomCenter' });
          this.resetSelectedAll();
          this.currentCbs = [];
          const page = this.reportData?.current_page ?? 1;
          this.searchData = { ...this.getReportParams(page, true) };
          this.getClassScoreSummaryReport(page, true);
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$store.commit('logout');
            window.location.replace('login');
            return;
          }
          const data = error.response?.data;
          const msg =
            (data?.errors?.items && (Array.isArray(data.errors.items) ? data.errors.items[0] : data.errors.items)) ||
            data?.message ||
            'Could not update display status.';
          this.$notification?.error?.(msg, { timer: 5, position: 'bottomCenter' });
          this.isLoading = false;
        });
    },

    search() {
      if (!this.selectedParentPeriod && !this.selectedTermId) {
        this.$notification?.error?.('Please select Academic Year or Term.', { timer: 3, position: 'bottomCenter' });
        return;
      }
      if (!this.selectedCampusGradeId || this.selectedCampusGradeId === 0) {
        this.$notification?.error?.('At least Grade is required for search.', { timer: 3, position: 'bottomCenter' });
        return;
      }
      this.searchData = { ...this.getReportParams(1, true) };
      this.getClassScoreSummaryReport(1, true);
    },

    onPaginateTo(page) {
      this.getClassScoreSummaryReport(page);
    },

    async reset() {
      this.isLoading = true;
      this.clearReport();
      this.selectedParentPeriod = null;
      this.selectedTermId = null;
      this.selectedCampusGradeId = null;
      this.selectedClassId = null;
      this.sortByField = 'student_no';
      this.sortOrder = 'asc';
      this.termArr = [];
      this.gradeArr = [];
      this.classArr = [];
      await this.getAcademicYear();
      await this.getCurrentParentAcademicYear();
      this.isLoading = false;
    },

    getReportParams(page = 1, isSearch = false) {
      const params = {};
      if (this.selectedTermId) params.term_id = this.selectedTermId;
      if (this.selectedParentPeriod) params.acd_year_id = this.selectedParentPeriod;
      if (this.selectedCampusGradeId && this.selectedCampusGradeId !== 0) params.campus_grade_id = this.selectedCampusGradeId;
      if (this.selectedClassId && this.selectedClassId !== 0) params.class_id = this.selectedClassId;
      params.sort_by = this.sortByField;
      params.sort_order = this.sortOrder;
      if (page != null) {
        params.page = page;
        params.per_page = this.perPage;
      }
      if (isSearch) params.refresh = 1;
      return params;
    },

    getClassScoreSummaryReport(page = 1, isSearch = false) {
      if (!this.searchData) return;
      this.isLoading = true;
      if (isSearch) {
        this.resetSelectedAll();
        this.currentCbs = [];
      }
      const params = { ...this.searchData };
      if (!isSearch) {
        delete params.refresh;
      }
      params.sort_by = this.sortByField;
      params.sort_order = this.sortOrder;
      if (page != null) {
        params.page = page;
        params.per_page = this.perPage;
      }
      axios
        .get('/class-score-summary/list', { params })
        .then((response) => {
          this.reportData = response.data;
          this.buildTableColumns();
          this.buildTableRows();
          this.$nextTick(() => {
            this.initCheckboxesForRows();
          });
          this.isLoading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
        });
    },

    buildTableColumns() {
      if (!this.reportData) {
        this.columns = [];
        return;
      }
      const cols = [
        { label: 'Student ID', field: 'student_no', sortable: true, type: 'text', columnClass: 'score-summary-stu-col' },
        { label: 'Student Name', field: 'student_name', sortable: true, type: 'text', columnClass: 'score-summary-stu-col' },
        { label: 'Status', field: 'status_label', sortable: true, type: 'status', columnClass: 'score-summary-status-col' },
      ];
      const subjects = this.reportData.subject_columns || [];
      subjects.forEach((sub) => {
        cols.push({
          label: sub,
          field: sub,
          sortable: true,
          type: 'number',
          formatFn: (val) => (val != null ? String(val) : '–'),
          tdClass: (row) => this.scoreCellClass(row[sub]),
        });
      });
      cols.push({
        label: 'Average',
        field: 'average',
        sortable: true,
        type: 'number',
        formatFn: (val) => (val != null ? String(val) : '–'),
        tdClass: (row) => this.scoreCellClass(row.average),
      });
      this.columns = cols;
    },

    buildTableRows() {
      if (!this.reportData || !this.reportData.students) {
        this.rows = [];
        return;
      }
      this.rows = this.reportData.students.map((s) => {
        const row = {
          student_id: s.student_id,
          class_id: s.class_id,
          student_no: s.student_no,
          student_name: s.student_name,
          status_label: s.is_display ? this.config.REPORT_CARD_DISPLAY_STATUS[1] : this.config.REPORT_CARD_DISPLAY_STATUS[2],
          average: s.average,
        };
        (this.reportData.subject_columns || []).forEach((sub) => {
          row[sub] = s[sub] != null ? s[sub] : null;
        });
        return row;
      });
    },

    scoreCellClass(value) {
      if (value == null) return '';
      const n = Number(value);
      if (!Number.isFinite(n)) return '';
      if (n >= 85) return 'score-high';
      if (n >= 75) return 'score-mid';
      return 'score-low';
    },

    sortBy(field) {
      if (this.sortByField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortByField = field;
        this.sortOrder = 'asc';
      }
      if (this.isPaginated && this.searchData) {
        this.getClassScoreSummaryReport(1);
      } else {
        this.$nextTick(() => {
          this.initCheckboxesForRows();
        });
      }
    },

    getScoreCellClass(col, row) {
      if (col.type === 'status') return '';
      if (col.type === 'number') {
        return this.scoreCellClass(row[col.field]);
      }
      return '';
    },

    getAverageHighlightClass(col, row) {
      if (col.type === 'status') return '';
      if (col.field !== 'average') return '';
      const avg = row.average;
      if (avg == null || !this.reportData) return '';
      const num = Number(avg);
      if (!Number.isFinite(num)) return '';
      if (this.highestMark !== null && num === this.highestMark) return 'score-avg-highest';
      if (this.lowestMark !== null && num === this.lowestMark) return 'score-avg-lowest';
      return '';
    },

    formatCellValue(val) {
      return val != null ? String(val) : '–';
    },

    excelDownload() {
      if (!this.selectedParentPeriod && !this.selectedTermId) {
        this.$notification?.error?.('Please select Academic Year or Term then Search before downloading.', { timer: 3, position: 'bottomCenter' });
        return;
      }
      if (!this.selectedCampusGradeId || this.selectedCampusGradeId === 0) {
        this.$notification?.error?.('At least Grade is required for search.', { timer: 3, position: 'bottomCenter' });
        return;
      }
      this.isLoading = true;
      const params = { ...this.searchData };
      delete params.page;
      delete params.per_page;
      params.sort_by = this.sortByField;
      params.sort_order = this.sortOrder;
      axios
        .get('/class-score-summary/excelDownload', {
          params,
          responseType: 'arraybuffer',
        })
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement('a');
          fileLink.href = fileURL;
          const date = new Date();
          const pad = (n) => (n < 10 ? '0' + n : n);
          const filename = `class_score_summary_${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}.xlsx`;
          fileLink.setAttribute('download', filename);
          document.body.appendChild(fileLink);
          fileLink.click();
          document.body.removeChild(fileLink);
          window.URL.revokeObjectURL(fileURL);
          this.isLoading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
        });
    },

    getAcademicYear() {
      axios
        .get('period/periodList')
        .then((res) => {
          this.parentPeriodArr = res.data.data || [];
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            store.commit('logout');
            window.location.replace('login');
          }
        });
    },

    getCurrentParentAcademicYear() {
      return axios
        .get('period/getThisPeriod')
        .then(async (res) => {
          if (!res.data || !res.data.id) return;
          const data = res.data;
          this.selectedParentPeriod = data.id ?? '';
          await this.getTermPeriod(data.id);
        })
        .catch(() => {});
    },

    getTermPeriod(acdYearId) {
      if (!acdYearId) {
        this.termArr = [];
        return Promise.resolve();
      }
      return axios
        .get('period/'+ acdYearId +'/term/list')
        .then((res) => {
          const raw = res.data.terms || [];
          this.termArr = raw.map((t) => ({ id: t.id, text: t.term_name || t.text || 'Term ' + t.id }));
        })
        .catch(() => {
          this.termArr = [];
        });
    },

    getGradeListByAcdYear(acdYearId) {
      if (!acdYearId) {
        this.gradeArr = [];
        return;
      }
      axios
        .get('campusGrade/listByAcdYear', { params: { acd_year_id: acdYearId } })
        .then((res) => {
          const data = res.data.data || [];
          this.gradeArr = data;
        })
        .catch(() => {
          this.gradeArr = [];
        });
    },

    getClassByCampusGrade(campusGradeId) {
      if (!campusGradeId) {
        this.classArr = [];
        return;
      }
      axios
        .get('class/getCampusGradeClass/' + campusGradeId)
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : [];
          this.classArr = [{ id: 0, text: 'All (Class)' }].concat(
            list.map((c) => ({ id: c.id, text: c.section || 'Class ' + c.id }))
          );
        })
        .catch(() => {
          this.classArr = [];
        });
    },
  },

  async mounted() {
    this.isLoading = true;
    await this.getAcademicYear();
    await this.getCurrentParentAcademicYear();
    this.isLoading = false;
  },
};
